import { captureException } from '@sentry/react';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { CustomError, ErrorCode } from 'src/error';
import { SupplierSelfBillStatus } from 'src/types/enums';
import {
  approveSelfBills as approveSelfBillsRequest,
  convertSelfBillsToPurchases as convertSelfBillsToPurchasesRequest,
  discardSelfBillDrafts as discardSelfBillDraftsRequest,
  type SelfBillListEntry,
} from '../../models';
import type { ConvertSelfBillsToPurchasesResponse } from '../../endpoints/SelfBill.types';
import { getDisplayError } from '@utils/get-display-error';
import { countSelfBills, type ISelfBillKindConfig } from '../self-bill-kind-config';

type SelfBillRow = SelfBillListEntry;

interface IUseSelfBillBulkActionsArgs {
  config: ISelfBillKindConfig;
  selections: SelfBillRow[];
  onSettled: () => void;
}

interface IDialogControls {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

function getConversionFailureMessage(
  config: ISelfBillKindConfig,
  result: ConvertSelfBillsToPurchasesResponse,
): string | null {
  const failed = result.outcomes.filter((outcome) => outcome.status === 'failed');
  if (!failed.length) {
    return null;
  }

  const prefix =
    result.createdCount > 0
      ? `${result.createdCount} aankoopfactu${result.createdCount === 1 ? 'ur' : 'ren'} aangemaakt, maar `
      : '';

  const shownFailures = failed.slice(0, 5);
  const details = shownFailures
    .map((outcome) => {
      const label = outcome.invoiceNumber ?? outcome.entryNumber ?? outcome.selfBillId;
      return `${label}: ${outcome.message ?? 'Onbekende fout'}`;
    })
    .join('\n');
  const remainingCount = failed.length - shownFailures.length;
  const remaining = remainingCount > 0 ? `\n... en ${remainingCount} meer` : '';

  return `${prefix}${countSelfBills(config, failed.length)} niet omgezet:\n${details}${remaining}`;
}

export interface ISelfBillBulkActions {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  isBusy: boolean;
  draftCount: number;
  hasDraft: boolean;
  sentCount: number;
  hasSent: boolean;
  approveDialog: IDialogControls;
  convertToPurchasesDialog: IDialogControls;
  handleApprove: (andSend: boolean) => Promise<void>;
  handleDiscard: () => Promise<void>;
  handleConvertToPurchases: () => Promise<void>;
}

export function useSelfBillBulkActions(args: IUseSelfBillBulkActionsArgs): ISelfBillBulkActions {
  const { config, selections, onSettled } = args;
  const [isOpen, setIsOpen] = useState(false);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isConvertToPurchasesOpen, setIsConvertToPurchasesOpen] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const draftIds = useMemo(
    () => selections.filter((s) => s.status === SupplierSelfBillStatus.Draft).map((s) => s.id),
    [selections],
  );
  const draftCount = draftIds.length;
  const hasDraft = draftCount > 0;
  // Approved and still without an AKF: a self-billing invoice normally gets one on approval.
  const sentIds = useMemo(
    () => selections.filter((s) => s.status === SupplierSelfBillStatus.Sent && !s.linkedPurchase).map((s) => s.id),
    [selections],
  );
  const sentCount = sentIds.length;
  const hasSent = sentCount > 0;

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => {
    if (!isBusy) setIsOpen(false);
  }, [isBusy]);

  const openApprove = useCallback(() => setIsApproveOpen(true), []);
  const closeApprove = useCallback(() => {
    if (!isBusy) setIsApproveOpen(false);
  }, [isBusy]);

  const openConvertToPurchases = useCallback(() => setIsConvertToPurchasesOpen(true), []);
  const closeConvertToPurchases = useCallback(() => {
    if (!isBusy) setIsConvertToPurchasesOpen(false);
  }, [isBusy]);

  const handleApprove = useCallback(
    async (andSend: boolean) => {
      if (!draftIds.length) {
        toast.error('Selecteer minstens één concept');
        return;
      }
      setIsBusy(true);
      try {
        const outcomes = await approveSelfBillsRequest(draftIds, andSend);
        const failed = outcomes.filter((o) => o.status === SupplierSelfBillStatus.Failed);
        const verb = andSend ? 'goedgekeurd & verzonden' : 'goedgekeurd';
        if (failed.length) {
          toast.error(`${outcomes.length - failed.length} ${verb}, ${failed.length} mislukt — zie status kolom`);
        } else {
          toast.success(`${countSelfBills(config, outcomes.length)} ${verb}`);
        }
        setIsApproveOpen(false);
        setIsOpen(false);
        onSettled();
      } catch (err) {
        captureException(err);
        toast.error(`Kon ${config.pluralName} niet goedkeuren: ${getDisplayError(err)}`);
        throw err;
      } finally {
        setIsBusy(false);
      }
    },
    [config, draftIds, onSettled],
  );

  const handleDiscard = useCallback(async () => {
    if (!draftIds.length) {
      toast.error('Selecteer minstens één concept');
      return;
    }
    setIsBusy(true);
    try {
      const result = await discardSelfBillDraftsRequest(draftIds);
      const count = result.count;
      toast.success(`${count} concept${count === 1 ? '' : 'en'} verwijderd`);
      setIsOpen(false);
      onSettled();
    } catch (err) {
      captureException(err);
      toast.error(`Kon concepten niet verwijderen: ${getDisplayError(err)}`);
    } finally {
      setIsBusy(false);
    }
  }, [draftIds, onSettled]);

  const handleConvertToPurchases = useCallback(async () => {
    if (!sentIds.length) {
      toast.error(`Selecteer minstens één verzonden ${config.documentName.toLowerCase()} zonder aankoopfactuur`);
      return;
    }
    setIsBusy(true);
    let handledResultError = false;
    try {
      const result = await convertSelfBillsToPurchasesRequest(sentIds);
      const failureMessage = getConversionFailureMessage(config, result);
      if (failureMessage) {
        handledResultError = true;
        onSettled();
        toast.error(failureMessage);
        throw new CustomError(failureMessage, ErrorCode.VALIDATION_ERROR);
      }

      toast.success(
        `${result.createdCount} aankoopfacturen aangemaakt. ${countSelfBills(config, result.skippedCount)} overgeslagen.`,
      );
      setIsConvertToPurchasesOpen(false);
      setIsOpen(false);
      onSettled();
    } catch (err) {
      if (!handledResultError) {
        captureException(err);
        toast.error(`Kon ${config.pluralName} niet omzetten: ${getDisplayError(err)}`);
      }
      throw err;
    } finally {
      setIsBusy(false);
    }
  }, [config, sentIds, onSettled]);

  return {
    isOpen,
    open,
    close,
    isBusy,
    draftCount,
    hasDraft,
    sentCount,
    hasSent,
    approveDialog: { isOpen: isApproveOpen, open: openApprove, close: closeApprove },
    convertToPurchasesDialog: {
      isOpen: isConvertToPurchasesOpen,
      open: openConvertToPurchases,
      close: closeConvertToPurchases,
    },
    handleApprove,
    handleDiscard,
    handleConvertToPurchases,
  };
}
